import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { BrandingProvider } from './context/BrandingContext';
import { DashboardLayout } from './components/DashboardLayout';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import KYCPage from './pages/KYCPage';
import KYCStatusPage from './pages/KYCStatusPage';
import MerchantDashboard from './pages/MerchantDashboard';
import TransactionsPage from './pages/TransactionsPage';
import SettlementsPage from './pages/SettlementsPage';
import APIConfigPage from './pages/APIConfigPage';
import AdminDashboard from './pages/AdminDashboard';
import MerchantsAdminPage from './pages/MerchantsAdminPage';
import KYCReviewPage from './pages/KYCReviewPage';
import ReconciliationPage from './pages/ReconciliationPage';
import AuditLogsPage from './pages/AuditLogsPage';

// Payout Gateway Pages
import WalletPage from './pages/WalletPage';
import WalletRechargePage from './pages/WalletRechargePage';
import RechargeSuccessPage from './pages/RechargeSuccessPage';
import PayoutsPage from './pages/PayoutsPage';
import CreatePayoutPage from './pages/CreatePayoutPage';
import BeneficiariesPage from './pages/BeneficiariesPage';
import AdminPayoutsPage from './pages/AdminPayoutsPage';
import AdminFundingPage from './pages/AdminFundingPage';
import AdminRoutingPage from './pages/AdminRoutingPage';
import AdminAuditLogsPage from './pages/AdminAuditLogsPage';
import AdminWalletLedgerPage from './pages/AdminWalletLedgerPage';
import AdminBrandingPage from './pages/AdminBrandingPage';
import AdminBulkPayoutsPage from './pages/AdminBulkPayoutsPage';
import AdminFeeManagementPage from './pages/AdminFeeManagementPage';
import AdminResellersPage from './pages/AdminResellersPage';
import AdminResellerCommissionsPage from './pages/AdminResellerCommissionsPage';
import ResellerDashboardPage from './pages/ResellerDashboardPage';
import ResellerMerchantsPage from './pages/ResellerMerchantsPage';
import ResellerCommissionsPage from './pages/ResellerCommissionsPage';
import ResellerWithdrawalsPage from './pages/ResellerWithdrawalsPage';
import AdminResellerWithdrawalsPage from './pages/AdminResellerWithdrawalsPage';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, resellerOnly = false }) => {
  const { user, loading, isAdmin, isReseller } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={adminOnly ? "/admin/login" : "/login"} replace />;
  }
  
  if (adminOnly && !isAdmin()) {
    return <Navigate to={isReseller() ? "/reseller" : "/dashboard"} replace />;
  }

  if (resellerOnly && !isReseller()) {
    return <Navigate to={isAdmin() ? "/admin" : "/dashboard"} replace />;
  }
  
  // Merchant trying to access merchant pages while being an admin - that's fine
  // Admin trying to access admin pages - that's fine
  
  return children;
};

// Admin Auth Route - redirects logged-in admins to admin dashboard, non-admins to merchant login
const AdminAuthRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    // If user is admin, redirect to admin dashboard
    if (isAdmin()) {
      return <Navigate to="/admin" replace />;
    }
    // If user is merchant, they shouldn't be on admin login page - redirect to merchant dashboard
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Merchant Auth Route - for merchant login page
const MerchantAuthRoute = ({ children }) => {
  const { user, loading, isAdmin, isReseller } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    // Redirect based on role
    return <Navigate to={isAdmin() ? '/admin' : isReseller() ? '/reseller' : '/dashboard'} replace />;
  }
  
  return children;
};

// Auth Route Component (redirect to dashboard if already logged in) - DEPRECATED, use specific routes
const AuthRoute = ({ children }) => {
  const { user, loading, isAdmin, isReseller } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={isAdmin() ? '/admin' : isReseller() ? '/reseller' : '/dashboard'} replace />;
  }
  
  return children;
};

// Analytics placeholder
const AnalyticsPage = () => {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold">Analytics</h1>
      <p className="text-muted-foreground">Detailed analytics and reporting coming soon...</p>
    </div>
  );
};

// Risk Management placeholder
const RiskManagementPage = () => {
  return <MerchantsAdminPage />;
};

// Admin Transactions placeholder
const AdminTransactionsPage = () => {
  const { api } = useAuth();
  const [transactions, setTransactions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/transactions/admin/all?limit=100');
        setTransactions(response.data);
      } catch (error) {
        console.error('Failed to fetch transactions');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);
  
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold">All Transactions</h1>
      <p className="text-muted-foreground">Platform-wide transaction monitoring</p>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="text-sm text-muted-foreground">
          {transactions.length} transactions found
        </div>
      )}
    </div>
  );
};

// Admin Settlements placeholder
const AdminSettlementsPage = () => {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold">Settlement Management</h1>
      <p className="text-muted-foreground">Generate and manage merchant settlements</p>
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Merchant Auth Routes */}
      <Route path="/login" element={
        <MerchantAuthRoute>
          <LoginPage />
        </MerchantAuthRoute>
      } />
      <Route path="/register" element={
        <MerchantAuthRoute>
          <RegisterPage />
        </MerchantAuthRoute>
      } />
      <Route path="/forgot-password" element={
        <AuthRoute>
          <ForgotPasswordPage />
        </AuthRoute>
      } />
      <Route path="/reset-password" element={
        <ResetPasswordPage />
      } />
      
      {/* Admin Auth Route - Separate Login for Admins */}
      <Route path="/admin/login" element={
        <AdminAuthRoute>
          <AdminLoginPage />
        </AdminAuthRoute>
      } />
      
      {/* Onboarding Route */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      
      {/* Merchant KYC Routes */}
      <Route path="/kyc" element={
        <ProtectedRoute>
          <DashboardLayout>
            <KYCPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/kyc/status" element={
        <ProtectedRoute>
          <DashboardLayout>
            <KYCStatusPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Merchant Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout>
            <MerchantDashboard />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/wallet" element={
        <ProtectedRoute>
          <DashboardLayout>
            <WalletPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/wallet/recharge" element={
        <ProtectedRoute>
          <DashboardLayout>
            <WalletRechargePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/wallet/recharge/success" element={
        <ProtectedRoute>
          <DashboardLayout>
            <RechargeSuccessPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/payouts" element={
        <ProtectedRoute>
          <DashboardLayout>
            <PayoutsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/payouts/create" element={
        <ProtectedRoute>
          <DashboardLayout>
            <CreatePayoutPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/beneficiaries" element={
        <ProtectedRoute>
          <DashboardLayout>
            <BeneficiariesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/transactions" element={
        <ProtectedRoute>
          <DashboardLayout>
            <TransactionsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/settlements" element={
        <ProtectedRoute>
          <DashboardLayout>
            <SettlementsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/analytics" element={
        <ProtectedRoute>
          <DashboardLayout>
            <AnalyticsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/api-config" element={
        <ProtectedRoute>
          <DashboardLayout>
            <APIConfigPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminDashboard />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/merchants" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <MerchantsAdminPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/kyc-review" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <KYCReviewPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/risk-management" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <RiskManagementPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/transactions" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminTransactionsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/settlements" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminSettlementsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/reconciliation" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <ReconciliationPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/payouts" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminPayoutsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/payouts/pending" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminPayoutsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/bulk-payouts" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminBulkPayoutsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/funding" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminFundingPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/routing" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminRoutingPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminAuditLogsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/wallet-ledger" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminWalletLedgerPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/branding" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminBrandingPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fees" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AdminFeeManagementPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/resellers" element={
        <ProtectedRoute adminOnly><DashboardLayout><AdminResellersPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/admin/reseller-commissions" element={
        <ProtectedRoute adminOnly><DashboardLayout><AdminResellerCommissionsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/admin/reseller-withdrawals" element={
        <ProtectedRoute adminOnly><DashboardLayout><AdminResellerWithdrawalsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/reseller" element={
        <ProtectedRoute resellerOnly><DashboardLayout><ResellerDashboardPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/reseller/merchants" element={
        <ProtectedRoute resellerOnly><DashboardLayout><ResellerMerchantsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/reseller/commissions" element={
        <ProtectedRoute resellerOnly><DashboardLayout><ResellerCommissionsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/reseller/withdrawals" element={
        <ProtectedRoute resellerOnly><DashboardLayout><ResellerWithdrawalsPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AuditLogsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <BrandingProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrandingProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
